import React, { useState } from "react";
import Icon from "./icons";
import { clockLabel, stepChips, stepCountLabel, stepVisual, stepsSummary } from "../../../lib/artemis/present";

// Protocol 2 steps above a reply (WEB-7094): a live "Working" row with one row per step,
// updated in place, while Artemis works; folded into one line once the answer starts, and
// reopenable. Reasoning text sits in its step behind a closed "Reasoning" disclosure: it is
// Artemis's summary of its own reasoning, not a transcript, so it is never styled as a quote
// or announced.

const statusLabel = status => (status === 'started' ? 'in progress' : status === 'failed' ? 'did not work' : status === 'stopped' ? 'stopped' : 'done')

const StepRow = ({ step: sent, tools, last, streaming }) => {
  const [reasonOpen, setReasonOpen] = useState(false)
  // A step still running when the reply ended (stopped or failed) never finished.
  const step = sent.status === 'started' && !streaming ? { ...sent, status: 'stopped' } : sent
  const { icon, tone } = stepVisual(step)
  const thinking = step.kind === 'thinking'
  const chips = stepChips(step, tools)

  return (
    <li className={`artemis-step artemis-step-${step.status}`}>
      {!last && <span className="artemis-step-line" aria-hidden="true" />}
      <span className={`artemis-step-dot artemis-tone-${tone}`}>
        {step.status === 'started' && <span className="artemis-step-spinner" aria-hidden="true" />}
        <Icon name={icon} size={14} />
      </span>
      <div className="artemis-step-body">
        {thinking ? (
          step.thinking ? (
            <button type="button" className="artemis-step-reason-toggle" aria-expanded={reasonOpen} onClick={() => setReasonOpen(!reasonOpen)}>
              {step.title && step.title !== 'Reasoning' ? step.title : 'Reasoning'}
              <Icon name={reasonOpen ? 'chevronUp' : 'chevronDown'} size={14} />
              <span className="artemis-visually-hidden">, {statusLabel(step.status)}</span>
            </button>
          ) : (
            <span className="artemis-step-title">{step.title || 'Reasoning'}<span className="artemis-visually-hidden">, {statusLabel(step.status)}</span></span>
          )
        ) : (
          <span className="artemis-step-title">
            {step.title || 'Working'}
            <span className="artemis-visually-hidden">, {statusLabel(step.status)}</span>
          </span>
        )}
        {step.summary && <span className="artemis-step-summary">{step.summary}</span>}
        {step.status === 'failed' && step.message && <span className="artemis-step-summary">{step.message.replace(/_/g, ' ')}</span>}
        {thinking && reasonOpen && step.thinking && <p className="artemis-step-reasoning">{step.thinking}</p>}
        {chips.length > 0 && (
          <div className="artemis-step-chips">
            {chips.map((chip, i) => <span key={i} className="artemis-step-chip">{chip}</span>)}
          </div>
        )}
      </div>
    </li>
  )
}

const AgentSteps = ({ message, now }) => {
  // null: follow the reply (open while working, folded once the answer starts); true / false: the user's choice.
  const [open, setOpen] = useState(null)
  const steps = message.steps || []
  const tools = message.tools || []
  // 'saving': a reply still running elsewhere (a partner's, in a group), polled as it goes.
  const streaming = message.status === 'streaming' || message.status === 'saving'
  const answering = !!message.answer
  const working = streaming && !answering

  if (!working && steps.length === 0 && tools.length === 0) return null

  const expanded = steps.length > 0 && (open !== null ? open : working)
  const latest = [...steps].reverse().find(s => s.kind !== 'thinking') || steps[steps.length - 1]
  const toggle = () => setOpen(!expanded)

  return (
    <div className="artemis-steps">
      {/* Announces step changes politely; the answer itself is not read out chunk by chunk. */}
      <div className="artemis-visually-hidden" aria-live="polite">
        {streaming && latest ? `${latest.title || 'Working'}, ${statusLabel(latest.status)}` : ''}
      </div>

      {working ? (
        <div className="artemis-working-row">
          <span className="artemis-working-dot" aria-hidden="true" />
          <span className="artemis-working-label">Working</span>
          <span className="artemis-working-meta">
            {clockLabel(now - (message.startedAt || now))}{steps.length > 0 ? ` · ${stepCountLabel(steps)}` : ''}
          </span>
          <span className="artemis-flex" />
          {steps.length > 0 && (
            <button type="button" className="artemis-text-button" aria-expanded={expanded} onClick={toggle}>
              {expanded ? 'Hide steps' : 'Show steps'}
              <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={14} />
            </button>
          )}
        </div>
      ) : (
        <button type="button" className="artemis-steps-toggle" aria-expanded={expanded} onClick={toggle} disabled={steps.length === 0}>
          {stepsSummary(message, now)}
          {steps.length > 0 && <Icon name={expanded ? 'chevronUp' : 'chevronRight'} size={14} />}
        </button>
      )}

      {expanded && (
        <ol className="artemis-steps-list">
          {steps.map((step, i) => <StepRow key={step.stepId} step={step} tools={tools} last={i === steps.length - 1} streaming={streaming} />)}
        </ol>
      )}

      {working && message.statusText && <p className="artemis-status-line">{message.statusText}</p>}
    </div>
  )
}

export default AgentSteps
